import { Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";

const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Produktportfolio = lazy(() => import("./pages/Produktportfolio.tsx"));
const Referenzen = lazy(() => import("./pages/Referenzen.tsx"));
const Faq = lazy(() => import("./pages/Faq.tsx"));
const UeberUns = lazy(() => import("./pages/UeberUns.tsx"));
const Mieterstrom = lazy(() => import("./pages/Mieterstrom.tsx"));
const Impressum = lazy(() => import("./pages/Impressum.tsx"));
const Datenschutz = lazy(() => import("./pages/Datenschutz.tsx"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy.tsx"));
const Kontakt = lazy(() => import("./pages/Kontakt.tsx"));
const Empfehlungen = lazy(() => import("./pages/Empfehlungen.tsx"));
const Agb = lazy(() => import("./pages/Agb.tsx"));
const Bestandsanlagen = lazy(() => import("./pages/Bestandsanlagen.tsx"));
const MehrAlsPv = lazy(() => import("./pages/MehrAlsPv.tsx"));
const Notstrom = lazy(() => import("./pages/Notstrom.tsx"));
const Aktuelles = lazy(() => import("./pages/Aktuelles.tsx"));
const SolarteurInDerNaehe = lazy(() => import("./pages/SolarteurInDerNaehe.tsx"));
const AktuellesDetail = lazy(() => import("./pages/AktuellesDetail.tsx"));
const AnlageKonfigurieren = lazy(() => import("./pages/AnlageKonfigurieren.tsx"));
const StandortWiesloch = lazy(() => import("./pages/standorte/Wiesloch.tsx"));
const StandortHeidelberg = lazy(() => import("./pages/standorte/Heidelberg.tsx"));
const StandortMannheim = lazy(() => import("./pages/standorte/Mannheim.tsx"));
const StandortWalldorf = lazy(() => import("./pages/standorte/Walldorf.tsx"));
const StandortSinsheim = lazy(() => import("./pages/standorte/Sinsheim.tsx"));
const StandortSchwetzingen = lazy(() => import("./pages/standorte/Schwetzingen.tsx"));
const StandortHockenheim = lazy(() => import("./pages/standorte/Hockenheim.tsx"));
const StandortBruchsal = lazy(() => import("./pages/standorte/Bruchsal.tsx"));
const StandortWeinheim = lazy(() => import("./pages/standorte/Weinheim.tsx"));
const StandortLeimen = lazy(() => import("./pages/standorte/Leimen.tsx"));
const StandortSandhausen = lazy(() => import("./pages/standorte/Sandhausen.tsx"));
const StandortEppelheim = lazy(() => import("./pages/standorte/Eppelheim.tsx"));
const StandortDielheim = lazy(() => import("./pages/standorte/Dielheim.tsx"));
const StandortHorrenberg = lazy(() => import("./pages/standorte/Horrenberg.tsx"));
const StandortWaghaeusel = lazy(() => import("./pages/standorte/Waghaeusel.tsx"));
const StandortLadenburg = lazy(() => import("./pages/standorte/Ladenburg.tsx"));
const StandortGaiberg = lazy(() => import("./pages/standorte/Gaiberg.tsx"));
const StandortMeckesheim = lazy(() => import("./pages/standorte/Meckesheim.tsx"));
const StandortRauenberg = lazy(() => import("./pages/standorte/Rauenberg.tsx"));
const StandortOftersheim = lazy(() => import("./pages/standorte/Oftersheim.tsx"));
const StandortPlankstadt = lazy(() => import("./pages/standorte/Plankstadt.tsx"));
const StandortMuehlhausen = lazy(() => import("./pages/standorte/Muehlhausen.tsx"));
const StandortStLeonRot = lazy(() => import("./pages/standorte/StLeonRot.tsx"));
const StandortBruehl = lazy(() => import("./pages/standorte/Bruehl.tsx"));
const StandortKetsch = lazy(() => import("./pages/standorte/Ketsch.tsx"));
const StandortIlvesheim = lazy(() => import("./pages/standorte/Ilvesheim.tsx"));
const StandortFeudenheim = lazy(() => import("./pages/standorte/Feudenheim.tsx"));
const StandortKarlsruhe = lazy(() => import("./pages/standorte/Karlsruhe.tsx"));
const StandortHeilbronn = lazy(() => import("./pages/standorte/Heilbronn.tsx"));
const StandortLudwigshafen = lazy(() => import("./pages/standorte/Ludwigshafen.tsx"));
const StromspeicherNachruesten = lazy(() => import("./pages/leistungen/StromspeicherNachruesten.tsx"));
const PvFuerEinfamilienhaus = lazy(() => import("./pages/leistungen/PvFuerEinfamilienhaus.tsx"));
const SolaranlageMitSpeicherRheinNeckar = lazy(() => import("./pages/leistungen/SolaranlageMitSpeicherRheinNeckar.tsx"));
const WallboxHeidelberg = lazy(() => import("./pages/leistungen/WallboxHeidelberg.tsx"));
const StromkostenSenken = lazy(() => import("./pages/ratgeber/StromkostenSenken.tsx"));
const PhotovoltaikFoerderung2026 = lazy(() => import("./pages/ratgeber/PhotovoltaikFoerderung2026.tsx"));
const PvAnlageErweitern = lazy(() => import("./pages/ratgeber/PvAnlageErweitern.tsx"));
const PvAnlageMitWaermepumpe = lazy(() => import("./pages/ratgeber/PvAnlageMitWaermepumpe.tsx"));
const WallboxFoerderung2026 = lazy(() => import("./pages/foerderung/WallboxFoerderung2026.tsx"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-label="Lädt…" role="status" />
  </div>
);

const AppRoutes = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/mieterstrom" element={<Mieterstrom />} />
          <Route path="/produktportfolio" element={<Produktportfolio />} />
          <Route path="/referenzen" element={<Referenzen />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/ueber-uns" element={<UeberUns />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route path="/empfehlungen" element={<Empfehlungen />} />
          <Route path="/agb" element={<Agb />} />
          <Route path="/bestandsanlagen" element={<Bestandsanlagen />} />
          <Route path="/mehr-als-pv" element={<MehrAlsPv />} />
          <Route path="/notstrom" element={<Notstrom />} />
          <Route path="/aktuelles" element={<Aktuelles />} />
          <Route path="/solarteur-in-der-naehe" element={<SolarteurInDerNaehe />} />
          <Route path="/aktuelles/:slug" element={<AktuellesDetail />} />
          <Route path="/anlage-konfigurieren" element={<AnlageKonfigurieren />} />
          <Route path="/standorte/wiesloch" element={<StandortWiesloch />} />
          <Route path="/standorte/heidelberg" element={<StandortHeidelberg />} />
          <Route path="/standorte/mannheim" element={<StandortMannheim />} />
          <Route path="/standorte/walldorf" element={<StandortWalldorf />} />
          <Route path="/standorte/sinsheim" element={<StandortSinsheim />} />
          <Route path="/standorte/schwetzingen" element={<StandortSchwetzingen />} />
          <Route path="/standorte/hockenheim" element={<StandortHockenheim />} />
          <Route path="/standorte/bruchsal" element={<StandortBruchsal />} />
          <Route path="/standorte/weinheim" element={<StandortWeinheim />} />
          <Route path="/standorte/leimen" element={<StandortLeimen />} />
          <Route path="/standorte/sandhausen" element={<StandortSandhausen />} />
          <Route path="/standorte/eppelheim" element={<StandortEppelheim />} />
          <Route path="/standorte/dielheim" element={<StandortDielheim />} />
          <Route path="/standorte/horrenberg" element={<StandortHorrenberg />} />
          <Route path="/standorte/waghaeusel" element={<StandortWaghaeusel />} />
          <Route path="/standorte/ladenburg" element={<StandortLadenburg />} />
          <Route path="/standorte/gaiberg" element={<StandortGaiberg />} />
          <Route path="/standorte/meckesheim" element={<StandortMeckesheim />} />
          <Route path="/standorte/rauenberg" element={<StandortRauenberg />} />
          <Route path="/standorte/oftersheim" element={<StandortOftersheim />} />
          <Route path="/standorte/plankstadt" element={<StandortPlankstadt />} />
          <Route path="/standorte/muehlhausen" element={<StandortMuehlhausen />} />
          <Route path="/standorte/st-leon-rot" element={<StandortStLeonRot />} />
          <Route path="/standorte/bruehl" element={<StandortBruehl />} />
          <Route path="/standorte/ketsch" element={<StandortKetsch />} />
          <Route path="/standorte/ilvesheim" element={<StandortIlvesheim />} />
          <Route path="/standorte/feudenheim" element={<StandortFeudenheim />} />
          <Route path="/standorte/karlsruhe" element={<StandortKarlsruhe />} />
          <Route path="/standorte/heilbronn" element={<StandortHeilbronn />} />
          <Route path="/standorte/ludwigshafen" element={<StandortLudwigshafen />} />
          <Route path="/leistungen/stromspeicher-nachruesten" element={<StromspeicherNachruesten />} />
          <Route path="/leistungen/pv-fuer-einfamilienhaus" element={<PvFuerEinfamilienhaus />} />
          <Route path="/leistungen/solaranlage-mit-speicher-rhein-neckar" element={<SolaranlageMitSpeicherRheinNeckar />} />
          <Route path="/leistungen/wallbox-heidelberg" element={<WallboxHeidelberg />} />
          <Route path="/ratgeber/stromkosten-senken" element={<StromkostenSenken />} />
          <Route path="/ratgeber/photovoltaik-foerderung-2026" element={<PhotovoltaikFoerderung2026 />} />
          <Route path="/ratgeber/pv-anlage-erweitern" element={<PvAnlageErweitern />} />
          <Route path="/ratgeber/pv-anlage-mit-waermepumpe" element={<PvAnlageMitWaermepumpe />} />
          <Route path="/foerderung/wallbox-foerderung-2026" element={<WallboxFoerderung2026 />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </TooltipProvider>
  </QueryClientProvider>
);

export default AppRoutes;
